import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import authService from '../services/AuthService';
import adMobService from '../services/AdMobService';
import { useLoginModal } from '../context/LoginModalContext';
import SettingsModal from './SettingsModal';

const PUBLIC_NAV_ITEMS = [
  { path: '/', labelKey: 'nav.home', icon: <HomeIcon fontSize="small" /> },
  { path: '/results', labelKey: 'nav.results', icon: <EmojiEventsIcon fontSize="small" />, getPath: () => adMobService.getResultsPath(), activePaths: ['/results', '/ad'] },
  { path: '/history', labelKey: 'nav.history', icon: <HistoryIcon fontSize="small" /> },
  { path: '/leaderboard', labelKey: 'nav.leaderboard', icon: <LeaderboardIcon fontSize="small" /> },
];

const QUICK_NAV_PATHS = ['/', '/results', '/history', '/leaderboard', '/achievements', '/profile'];

const AUTH_NAV_ITEMS = [
  { path: '/achievements', labelKey: 'nav.achievements', icon: <MilitaryTechIcon fontSize="small" /> },
  { path: '/groups', labelKey: 'nav.groups', icon: <GroupsIcon fontSize="small" />, activePaths: ['/groups'] },
  { path: '/profile', labelKey: 'nav.profile', icon: <PersonIcon fontSize="small" /> },
];

const ADMIN_NAV_ITEMS = [
  { path: '/admin', labelKey: 'nav.admin', icon: <AdminPanelSettingsIcon fontSize="small" /> },
];

const TOOLBAR_GAP_PX = 8;
const FIT_SAFETY_BUFFER_PX = 16;

function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const openLoginModal = useLoginModal();

  const toolbarRef = useRef(null);
  const titleRef = useRef(null);
  const quickNavMeasureRef = useRef(null);

  const toolbarWidth = useElementWidth(toolbarRef);
  const titleWidth = useElementWidth(titleRef);
  const quickNavWidth = useElementWidth(quickNavMeasureRef);

  const isAuthenticated = authService.isAuthenticated();
  const isAdmin = authService.getUser()?.role === 'ADMIN';
  const navItems = isAuthenticated
    ? [...PUBLIC_NAV_ITEMS, ...AUTH_NAV_ITEMS, ...(isAdmin ? ADMIN_NAV_ITEMS : [])]
    : PUBLIC_NAV_ITEMS;
  const quickNavItems = navItems.filter((item) => QUICK_NAV_PATHS.includes(item.path));

  const halfShareForQuickNav = (toolbarWidth - titleWidth - TOOLBAR_GAP_PX * 2) / 2 - FIT_SAFETY_BUFFER_PX;
  const showQuickNav = isMobile && quickNavItems.length > 0 && quickNavWidth > 0 && halfShareForQuickNav >= quickNavWidth;

  const handleLogout = async () => {
    await authService.logout();
    navigate('/');
  };


  const navContent = (
    <>
      {navItems.map((item) => (
        <Button
          key={item.path}
          color={(item.activePaths ?? [item.path]).includes(location.pathname) ? 'secondary' : 'inherit'}
          startIcon={item.icon}
          onClick={() => {
            navigate(item.getPath ? item.getPath() : item.path);
            setDrawerOpen(false);
          }}
          sx={{ fontWeight: (item.activePaths ?? [item.path]).includes(location.pathname) ? 700 : 400 }}
        >
          {t(item.labelKey)}
        </Button>
      ))}
    </>
  );

  const mobileDrawerContent = (
    <Box sx={{ width: 240, pt: 2 }}>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={(item.activePaths ?? [item.path]).includes(location.pathname)}
              onClick={() => {
                navigate(item.getPath ? item.getPath() : item.path);
                setDrawerOpen(false);
              }}
            >
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                {item.icon}
              </Box>
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          {isAuthenticated ? (
            <ListItemButton onClick={handleLogout}>
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'error.main' }}>
                <LogoutIcon fontSize="small" />
              </Box>
              <ListItemText primary={t('nav.logout')} sx={{ color: 'error.main' }} />
            </ListItemButton>
          ) : (
            <ListItemButton onClick={() => { setDrawerOpen(false); openLoginModal(true); }}>
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <LoginIcon fontSize="small" />
              </Box>
              <ListItemText primary={t('auth.loginOrRegister')} sx={{ color: 'primary.main' }} />
            </ListItemButton>
          )}
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: '#ffffff',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        color: 'text.primary',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <Toolbar ref={toolbarRef} sx={{ gap: 1 }}>
        {isMobile && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {!showQuickNav && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setDrawerOpen(true)}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            )}

            {showQuickNav && quickNavItems.map((item) => (
              <Tooltip key={item.path} title={t(item.labelKey)}>
                <IconButton
                  color={(item.activePaths ?? [item.path]).includes(location.pathname) ? 'secondary' : 'inherit'}
                  onClick={() => navigate(item.getPath ? item.getPath() : item.path)}
                  aria-label={t(item.labelKey)}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}

        {isMobile && (
          <Box
            ref={quickNavMeasureRef}
            sx={{ position: 'fixed', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: 0.5 }}
          >
            {quickNavItems.map((item) => (
              <IconButton key={item.path} tabIndex={-1}>
                {item.icon}
              </IconButton>
            ))}
          </Box>
        )}

        <Typography
          ref={titleRef}
          variant="h6"
          component="div"
          sx={{ fontWeight: 700, color: 'primary.main', cursor: 'pointer', flexShrink: 0, ...(isMobile ? {} : { mr: 2 }) }}
          onClick={() => navigate('/')}
        >
          Concordare
        </Typography>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
            {navContent}
          </Box>
        )}

        <Box
          sx={isMobile
            ? { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minWidth: 0 }
            : { ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <IconButton
            onClick={() => setSettingsOpen(true)}
            color="inherit"
            size="small"
            aria-label={t('settings.title')}
          >
            <SettingsIcon />
          </IconButton>

          {isMobile ? (
            showQuickNav && (
              isAuthenticated ? (
                <Tooltip title={t('nav.logout')}>
                  <IconButton color="error" onClick={handleLogout} aria-label={t('nav.logout')}>
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('auth.loginOrRegister')}>
                  <IconButton color="primary" onClick={() => openLoginModal(true)} aria-label={t('auth.loginOrRegister')}>
                    <LoginIcon />
                  </IconButton>
                </Tooltip>
              )
            )
          ) : (
            isAuthenticated ? (
              <Button
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                variant="outlined"
              >
                {t('nav.logout')}
              </Button>
            ) : (
              <Button
                color="primary"
                size="small"
                startIcon={<LoginIcon />}
                onClick={() => openLoginModal(true)}
                variant="contained"
              >
                {t('auth.loginOrRegister')}
              </Button>
            )
          )}
        </Box>

        <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </Toolbar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        {mobileDrawerContent}
      </Drawer>
    </AppBar>
  );
}
